<template>
  <b-modal
    header-close-content=""
    class="multi-default-value-select-dialog"
    body-class="multi-default-value-select-dialog-body"
    :visible="visible"
    @hidden="$emit('update:visible', false)"
    @ok="onOkButtonClicked"
    :title="$t('multiSelectDefaultAttributeValues')"
    size="lg">
    <div class="container">
      <div class="row">
        <div class="col-sm-11">
          <table class="table table-sm" style="width: 100%">
            <colgroup>
              <col span="1" style="width: 5%" />
              <col span="1" style="width: 10%" />
              <col span="1" style="width: 85%" />
            </colgroup>
            <thead>
              <tr>
                <th class="checkbox-column"></th>
                <th :key="'value-uid'" class="name-column">
                  <span>
                    {{ $t("valueid") }}
                  </span>
                </th>
                <th :key="'value'">
                  {{ $t("value") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(currentValue, rowIndex) in values" :key="rowIndex">
                <td class="checkbox-column">
                  <b-form-checkbox v-model="selectedIndexes" :value="rowIndex" :id="currentValue.id" />
                </td>
                <td :key="'id-' + rowIndex">
                  <span>{{ currentValue.uid }}</span>
                </td>
                <td :key="'value-' + rowIndex" class="form-group d-flex">
                  <b-dropdown right class="ml-3 dropdown-default grow max-width-menu">
                    <template #button-content>
                      <span class="ellipsis" v-tooltip.ellipsis="currentValue.fallbackResolvedValue">
                        <em v-if="!currentValue.fallbackResolvedValue">&lt;{{ $t("emptyValue") }}&gt;</em>
                        {{ currentValue.fallbackResolvedValue }}
                      </span>
                    </template>
                    <b-dropdown-item active v-tooltip.right.ellipsis="currentValue.fallbackResolvedValue">
                      <span v-if="!currentValue.fallbackResolvedValue">
                        <em>&lt;{{ $t("emptyValue") }}&gt;</em>
                      </span>
                      {{ currentValue.fallbackResolvedValue }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-for="(item, index) in filteredOptions"
                      :key="index"
                      @click="values.splice(rowIndex, 1, item)"
                      v-tooltip.right.ellipsis="item.fallbackResolvedValue">
                      <span v-if="!item.fallbackResolvedValue">
                        <em>&lt;{{ $t("emptyValue") }}&gt;</em>
                      </span>
                      {{ item.fallbackResolvedValue }}
                    </b-dropdown-item>
                  </b-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="col-sm-1">
          <div class="btn-group-vertical">
            <b-btn
              type="button"
              v-tooltip.left="$t('moveFirst')"
              class="btn btn-sm"
              @click.prevent="moveTop"
              :disabled="selectedIndexes.length != 1 || selectedIndexes[0] == 0">
              <i class="cil-chevron-double-up-alt"></i>
            </b-btn>
            <b-btn
              type="button"
              v-tooltip.left="$t('moveUp')"
              class="btn btn-sm"
              @click.prevent="moveUp"
              :disabled="selectedIndexes.length == 0 || selectedIndexes.some((x) => x == 0)">
              <i class="mdp-ChevronTop"></i>
            </b-btn>
            <b-btn
              type="button"
              v-tooltip.left="$t('moveDown')"
              class="btn btn-sm"
              @click.prevent="moveDown"
              :disabled="selectedIndexes.length == 0 || selectedIndexes.some((x) => x == values.length - 1)">
              <i class="mdp-ChevronBottom"></i>
            </b-btn>
            <b-btn
              type="button"
              v-tooltip.left="$t('moveLast')"
              class="btn btn-sm"
              @click.prevent="moveBottom"
              :disabled="selectedIndexes.length != 1 || selectedIndexes[0] == values.length - 1">
              <i class="cil-chevron-double-down-alt"></i>
            </b-btn>
          </div>
        </div>
      </div>
    </div>
    <p class="mb-0">
      <button
        class="btn btn-with-icon mono mr-1"
        @click.prevent="addValues"
        :disabled="values.length == defaultValues.length"
        data-cy="multiselect-default-attribute-values-add-btn">
        <i class="mdp-Plus" />{{ $t("add") }}
      </button>
      <button class="btn btn-with-icon mono mr-1" @click.prevent="deleteValues" :disabled="!selectedIndexes.length">
        <i class="mdp-Delete" />{{ $t("delete") }}
      </button>
    </p>
    <template #modal-footer="{ ok, cancel }">
      <b-btn @click="ok" variant="primary" data-cy="multiselect-default-attribute-values-ok-btn">{{ $t("OK") }}</b-btn>
      <b-btn @click="cancel">{{ $t("Cancel") }}</b-btn>
    </template>
  </b-modal>
</template>

<script>
import _ from "lodash";
export default {
  props: ["visible", "defaultValues", "valueList", "language"],
  data() {
    return {
      isOpen: false,
      options: [],
      values: [],
      selectedIndexes: []
    };
  },
  methods: {
    arraymove(arr, fromIndex, toIndex) {
      const element = arr[fromIndex];
      arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, element);
    },
    moveUp() {
      this.$root.$emit("bv::hide::tooltip");
      const newSelectedPositions = [];
      this.selectedIndexes.sort((x, y) => x - y);
      this.selectedIndexes.forEach((position) => {
        const newPosition = position - 1;
        this.arraymove(this.values, position, newPosition);
        newSelectedPositions.push(newPosition);
      });
      this.selectedIndexes = newSelectedPositions;
    },
    moveTop() {
      this.$root.$emit("bv::hide::tooltip");
      this.arraymove(this.values, this.selectedIndexes[0], 0);
      this.selectedIndexes = [0];
    },
    moveBottom() {
      this.$root.$emit("bv::hide::tooltip");
      this.arraymove(this.values, this.selectedIndexes[0], this.values.length - 1);
      this.selectedIndexes = [this.values.length - 1];
    },
    moveDown() {
      this.$root.$emit("bv::hide::tooltip");
      const newSelectedPositions = [];
      this.selectedIndexes.sort((x, y) => y - x);
      this.selectedIndexes.forEach((position) => {
        const newPosition = position + 1;
        this.arraymove(this.values, position, newPosition);
        newSelectedPositions.unshift(newPosition);
      });
      this.selectedIndexes = newSelectedPositions;
    },
    addValues() {
      const item = this.filteredOptions[0];
      if (item) this.values.push(item);
    },
    deleteValues() {
      this.values = this.values.filter((itm, index) => !this.selectedIndexes.includes(index));
      this.selectedIndexes = [];
    },
    onOkButtonClicked() {
      this.$emit("valueList", this.values);
    }
  },
  watch: {
    valueList: {
      immediate: true,
      handler(nv) {
        if (!nv) this.values = [];
        else {
          this.values = _.cloneDeep(nv);
        }
      }
    }
  },
  computed: {
    filteredOptions() {
      return _.sortBy(
        this.defaultValues.filter((option) => this.values.every((selected) => selected.id != option.id)),
        ["sequence"]
      );
    }
  }
};
</script>
